<script setup>
import {computed, ref, watch} from "vue";
import moment from "moment";
import CustomToggle from "@/components/global-themed/CustomToggle.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton";
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import {useUntrackedKeywords, useUntrackedKeywordsFilters} from "@/composables/keywords/useUntrackedKeywords";

const {dates, isLoading} = useUntrackedKeywords();
const {dateFrom: dateFromValue, setDateFrom, setDateTo} = useUntrackedKeywordsFilters();

const dateFrom = computed({
  get: () => dateFromValue.value,
  set: (value) => setDateFrom(value),
});

const dateTo = ref(null);

const showDateTo = ref(false);

watch(showDateTo, (value) => {
  if (value) {
    return;
  }

  dateTo.value = null;
  setDateTo(null);
});

const datesTo = computed(() => {
  const selectedDate = moment(dateFrom.value);

  if (dates.value && Array.isArray(dates.value) && dates.value.length > 0) {
    return dates.value.filter(date =>
      !date.disabled
      && moment(date.value).isBefore(selectedDate)
      && !moment(date.value).isSame(selectedDate, 'month')
    );
  }

  return [];
});

const handleFilter = () => {
  setDateTo(dateTo.value);
};
</script>

<template>
  <div class="tw-bg-white tw-rounded-lg tw-p-8 tw-flex tw-space-y-8 tw-flex-1 tw-mb-5 tw-justify-center">
    <div class="tw-flex tw-gap-x-12 tw-items-end tw-w-full tw-max-w-7.5xl">
      <div class="tw-w-full tw-max-w-[460px]">
        <label
          for="dateFrom"
          class="tw-text-grey"
        >
          From
        </label>
        <CustomMultiselect
          id="dateFrom"
          v-model="dateFrom"
          :options="dates"
          :caret="false"
          class="tw-relative"
          :close-on-select="true"
          :searchable="false"
        >
          <template #icon>
            <svg-vue
              icon="icons/calendar"
              class="tw-w-6 tw-h-7 tw-mr-2 tw-text-grey-dark"
            />
          </template>
        </CustomMultiselect>
      </div>

      <div class="tw-flex tw-items-center tw-shrink-0 tw-mb-2.5 tw-gap-2">
        <CustomToggle
          v-model="showDateTo"
        />
        <span class="tw-text-sm tw-text-slate-200">Compare with</span>
      </div>

      <div
        class="tw-transition-all tw-duration-[300ms]"
        :class="[
          showDateTo ? 'tw-w-full tw-max-w-[460px] tw-opacity-100' : 'tw-w-0 tw-max-w-[460px] tw-opacity-0'
        ]"
      >
        <label
          for="dateTo"
          class="tw-text-grey"
        >
          To
        </label>
        <CustomMultiselect
          id="dateTo"
          v-model="dateTo"
          :options="datesTo"
          :caret="false"
          class="tw-relative"
          :close-on-select="true"
          :searchable="false"
        >
          <template #icon>
            <svg-vue
              icon="icons/calendar"
              class="tw-w-6 tw-h-7 tw-mr-2 tw-text-grey-dark"
            />
          </template>
        </CustomMultiselect>
      </div>

      <DefaultButton
        :disabled="isLoading || showDateTo && dateTo === null"
        additional-classes="tw-whitespace-nowrap tw-w-40"
        @click="handleFilter"
      >
        Apply Filter
      </DefaultButton>
    </div>
  </div>
</template>

